import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import {
  Stack,
  TextField,
  Button,
  Paper,
  LinearProgress,
  Alert,
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  Cell,
} from 'recharts';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { useTotalAlumnosUnicosReport } from '../../../hooks/useReportJob';
import { useTotalAlumnosUnicosSegmentoReport } from '../../../hooks/useReportJob';
import type { DistribAlumnosRow, DistribAluRow } from '../../../api/reports';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';
import ReportHelpButton from '../../../components/genericos/ReportHelpButton';

const COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#845EC2',
  '#4D8076',
  '#F65A83',
  '#2C73D2',
  '#D65DB1',
  '#FF9671',
];

const fmtInt = (n: number) => (Number.isFinite(n) ? n.toLocaleString() : '—');

interface MetricCardProps {
  label: string;
  value: string | number;
  highlight?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
}

function MetricCard({ label, value, highlight }: MetricCardProps) {
  return (
    <Card sx={{ minWidth: 200, flexGrow: 1 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h4" color={highlight ?? 'text.primary'}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

interface DistributionChartProps {
  title: string;
  data: DistribAlumnosRow[];
  dataKey: string;
  height?: number;
  onBarClick?: (entry: DistribAlumnosRow) => void;
}

function DistributionChart({
  title,
  data,
  dataKey,
  height = 300,
  onBarClick,
}: DistributionChartProps) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" allowDecimals={false} />
          <YAxis type="category" dataKey={dataKey} width={150} interval={0} />
          <RechartsTooltip formatter={(value: number) => fmtInt(value)} />
          <Legend />
          <Bar dataKey="numAlu" name="Nº Alumnos" fill="#8884d8">
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
                style={{ cursor: onBarClick ? 'pointer' : 'default' }}
                onClick={() => onBarClick?.(entry)}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}

export default function TotalAlumnosUnicosPage() {
  const { t } = useTranslation<'translation'>();
  const [desde, setDesde] = useState(dayjs().subtract(1, 'month').format('YYYY-MM-DD'));
  const [hasta, setHasta] = useState(dayjs().format('YYYY-MM-DD'));

  const { run, useStatus } = useTotalAlumnosUnicosReport();
  const { run: runDistrib, useStatus: useStatusDistrib } = useTotalAlumnosUnicosSegmentoReport();
  const [jobId, setJobId] = useState<string | undefined>();
  const { data: status } = useStatus(jobId);
  const data = status?.result?.result;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [distribJobId, setDistribJobId] = useState<string | undefined>();
  const { data: distribStatus } = useStatusDistrib(distribJobId);
  const distribRows = distribStatus?.result?.rows as DistribAluRow[] | undefined;
  const distribUrl = distribStatus?.result?.url as string | undefined;

  const launch = () => {
    setJobId(undefined);
    run({ desde, hasta }).then(({ jobId }) => setJobId(jobId));
  };

  const handleBarClick = (campo: 'proyecto' | 'grade' | 'formato' | 'centro', label: string) => {
    // campo debe mapearse desde el chart correspondiente en el render
    setDistribJobId(undefined);
    runDistrib({ campo, valor: label, desde, hasta }).then(({ jobId }) => {
      setDistribJobId(jobId);
      setDialogOpen(true);
    });
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error('Error al descargar:', error);
      }
    }
  };

  const invalidRange = useMemo(() => dayjs(desde).isAfter(dayjs(hasta)), [desde, hasta]);

  const isRunning = !!status && !status.done && !status.failed;
  const hasError = status?.failed;

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        spacing={1}
      >
        <Typography variant="h5">{t('totalAlumnosUnicos.titulo')}</Typography>
        <ReportHelpButton
          title="¿Cómo funciona este reporte?"
          intro="Este informe calcula alumnos únicos activos con reservas válidas dentro del rango seleccionado."
          sections={[
            {
              title: 'Regla de conteo',
              items: [
                'Cada alumno se cuenta una sola vez en el total, aunque tenga múltiples reservas.',
                'Solo se incluyen reservas en estado Confirmed.',
                'Se excluyen explícitamente las Recovery classes.',
                'Se incluyen solo proyectos que cuentan en reportes de alumnos.',
              ],
            },
            {
              title: 'Distribuciones',
              items: [
                'Las barras por Proyecto, Grade, Formato y Centro muestran alumnos únicos por dimensión.',
                'Al hacer clic en una barra, se abre el detalle de alumnos de ese segmento.',
              ],
            },
            {
              title: 'Interpretación rápida',
              items: [
                'El KPI principal representa el tamaño real de alumnado activo en el periodo.',
                'Comparar periodos ayuda a detectar crecimiento, estacionalidad y cambios de mezcla.',
              ],
            },
          ]}
        />
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          type="date"
          label={t('totalAlumnosUnicos.filtros.desde')}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          size="small"
        />
        <TextField
          type="date"
          label={t('totalAlumnosUnicos.filtros.hasta')}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          size="small"
        />
        <Button variant="contained" onClick={launch} disabled={isRunning || invalidRange}>
          {t('totalAlumnosUnicos.generar')}
        </Button>
        {invalidRange && (
          <Chip
            color="warning"
            label={t('reservasMetricas.filtros.rangoInvalido', 'Rango inválido')}
            size="small"
          />
        )}
      </Stack>

      {isRunning && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t('totalAlumnosUnicos.procesando', { progress: status.progress })}
          </Typography>
        </Stack>
      )}

      {hasError && <Alert severity="error">{t('totalAlumnosUnicos.error')}</Alert>}

      {status?.done && data && (
        <>
          <Alert
            severity="success"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
              >
                {t('totalAlumnosUnicos.descargar')}
              </Button>
            }
          >
            {t('totalAlumnosUnicos.resultados')}
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MetricCard
                label={t('totalAlumnosUnicos.total')}
                value={fmtInt(data.totalAlumnos)}
                highlight="primary"
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <DistributionChart
                title={t('totalAlumnosUnicos.porProyecto')}
                data={data.porProyecto}
                dataKey="label"
                onBarClick={(e) => handleBarClick('proyecto', e.label)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DistributionChart
                title={t('totalAlumnosUnicos.porGrade')}
                data={data.porGrade}
                dataKey="label"
                onBarClick={(e) => handleBarClick('grade', e.label)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DistributionChart
                title={t('totalAlumnosUnicos.porFormato')}
                data={data.porFormato}
                dataKey="label"
                onBarClick={(e) => handleBarClick('formato', e.label)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DistributionChart
                title={t('totalAlumnosUnicos.porCentro')}
                data={data.porCentro}
                dataKey="label"
                onBarClick={(e) => handleBarClick('centro', e.label)}
              />
            </Grid>
          </Grid>
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('totalAlumnosUnicos.alumnosLista', 'Alumnos')}</DialogTitle>
        <DialogContent dividers>
          {distribStatus && !distribStatus.done && <LinearProgress />}
          {distribRows && (
            <List>
              {distribRows.map((r) => (
                <ListItem key={r.id}>
                  {r.nombre} — {r.email ?? '—'} — {r.telefono ?? '—'}
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          {distribUrl && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => distribUrl && downloadReportFile(distribUrl)}
            >
              {t('totalAlumnosUnicos.descargarLista', 'Descargar Excel')}
            </Button>
          )}
          <Button onClick={() => setDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
